import React from 'react';
import { Shield, Sparkles, Tags } from 'lucide-react';

import { useMultiProject } from '@/features/projects/MultiProjectContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { DEVELOPMENT_ROLE_OPTIONS, VISIBILITY_TIER_OPTIONS, WORKSTREAM_OPTIONS } from '@/shared/constants/projectMetadata';
import type { DevelopmentRole, VisibilityTier, Workstream } from '@/shared/types/projectData';
import { MultiAssigneeInput } from '@/ui/primitives/inputs/MultiAssigneeInput';
import { cn } from '@/ui/theme/ThemeContext';
import { VisibilityApprovalRequestDialog } from './VisibilityApprovalRequestDialog';
import type { ApprovalRequestScope } from '../services/approvalRequestService';
import type { VisibilityActionOption } from '../utils/visibilityAuthority';

const VISIBILITY_OPTIONS: VisibilityActionOption[] = VISIBILITY_TIER_OPTIONS.map((value) => ({ value, mode: 'direct' }));

interface NodeMetadataEditorProps {
  projectId?: string | null;
  nodeId?: string | null;
  targetLabel?: string;
  workstream?: Workstream;
  developmentRoles?: DevelopmentRole[];
  visibilityTier?: VisibilityTier;
  visibleToAccountIds?: string[];
  classificationConfidence?: number | null;
  rationale?: string | null;
  readOnly?: boolean;
  visibilityOptions?: VisibilityActionOption[];
  canManageVisibleToAccounts?: boolean;
  suggesting?: boolean;
  showApplyToDescendants?: boolean;
  applyToDescendants?: boolean;
  theme: Record<string, string>;
  onWorkstreamChange: (value: Workstream) => void;
  onDevelopmentRolesChange: (value: DevelopmentRole[]) => void;
  onVisibilityTierChange: (value: VisibilityTier) => void;
  onRequestVisibilityTier?: (payload: {
    projectId: string;
    nodeId: string;
    visibilityTier: VisibilityTier;
    scope: ApprovalRequestScope;
    rationale: string;
  }) => Promise<void> | void;
  onVisibleToAccountIdsChange: (value: string[]) => void;
  onApplyToDescendantsChange?: (value: boolean) => void;
  onSuggest?: () => void;
}

export const NodeMetadataEditor: React.FC<NodeMetadataEditorProps> = ({
  projectId,
  nodeId,
  targetLabel,
  workstream,
  developmentRoles,
  visibilityTier,
  visibleToAccountIds,
  classificationConfidence,
  rationale,
  readOnly = false,
  visibilityOptions = VISIBILITY_OPTIONS,
  canManageVisibleToAccounts = false,
  suggesting = false,
  showApplyToDescendants = false,
  applyToDescendants = false,
  theme,
  onWorkstreamChange,
  onDevelopmentRolesChange,
  onVisibilityTierChange,
  onRequestVisibilityTier,
  onVisibleToAccountIdsChange,
  onApplyToDescendantsChange,
  onSuggest,
}) => {
  const { t } = useSettings();
  const { getAssigneeDisplayName, getProjectMembers } = useMultiProject();
  const [pendingRequestVisibilityTier, setPendingRequestVisibilityTier] = React.useState<VisibilityTier | null>(null);
  const [isSubmittingRequest, setIsSubmittingRequest] = React.useState(false);
  const [requestError, setRequestError] = React.useState<string | null>(null);
  const selectedVisibilityValue = `direct:${visibilityTier || 'internal'}`;
  const projectMembers = React.useMemo(() => getProjectMembers(projectId), [getProjectMembers, projectId]);
  const projectMemberIds = React.useMemo(
    () => projectMembers.map((user) => user.id),
    [projectMembers]
  );
  const visibleToCount = visibleToAccountIds?.length || 0;
  const selectedDevelopmentRoles = developmentRoles || [];

  const handleWorkstreamChange = (nextWorkstream: Workstream) => {
    onWorkstreamChange(nextWorkstream);
    if (nextWorkstream !== 'development' && selectedDevelopmentRoles.length > 0) {
      onDevelopmentRolesChange([]);
    }
  };

  const toggleDevelopmentRole = (role: DevelopmentRole) => {
    const nextRoles = selectedDevelopmentRoles.includes(role)
      ? selectedDevelopmentRoles.filter((entry) => entry !== role)
      : [...selectedDevelopmentRoles, role];
    onDevelopmentRolesChange(nextRoles);
  };

  const handleVisibilitySelection = (rawValue: string) => {
    const [mode, tierValue] = rawValue.split(':');
    const nextTier = (tierValue === 'partner' || tierValue === 'client' ? tierValue : 'internal') as VisibilityTier;

    if (mode === 'request') {
      setPendingRequestVisibilityTier(nextTier);
      return;
    }

    onVisibilityTierChange(nextTier);
  };

  const defaultRequestScope: ApprovalRequestScope =
    showApplyToDescendants && applyToDescendants ? 'subtree' : 'node';

  return (
    <>
      <div data-testid="node-metadata-editor" className={cn('space-y-3 rounded-xl border p-3', theme.bgSecondary, theme.borderPrimary)}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className={cn('text-sm font-semibold', theme.textPrimary)}>{t('Classification')}</div>
            <div className={cn('text-xs', theme.textSecondary)}>
              {t('Control workstream and client-safe visibility without changing the current visual theme.')}
            </div>
          </div>
          {!readOnly && onSuggest ? (
            <button
              type="button"
              data-testid="node-metadata-suggest-button"
              onClick={onSuggest}
              disabled={suggesting}
              className={cn('inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors', theme.hover, theme.textPrimary)}
            >
              <Sparkles size={14} />
              {suggesting ? t('Suggesting...') : t('Suggest')}
            </button>
          ) : null}
        </div>

        <div className="grid gap-3">
          <label className="space-y-1">
            <span className={cn('flex items-center gap-2 text-xs font-medium', theme.textSecondary)}>
              <Tags size={14} />
              {t('Workstream')}
            </span>
            <select
              data-testid="node-metadata-workstream-select"
              value={workstream || 'general'}
              onChange={(event) => handleWorkstreamChange(event.target.value as Workstream)}
              disabled={readOnly}
              className={cn('w-full rounded-lg border px-3 py-2 text-sm', theme.bgPrimary, theme.borderPrimary, theme.textPrimary)}
            >
              {WORKSTREAM_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {t(option)}
                </option>
              ))}
            </select>
          </label>

          {(workstream || 'general') === 'development' ? (
            <div className="space-y-2">
              <span className={cn('text-xs font-medium', theme.textSecondary)}>
                {t('Development roles')}
              </span>
              {readOnly ? (
                <div className={cn('w-full rounded-lg border px-3 py-2 text-sm', theme.bgPrimary, theme.borderPrimary, theme.textPrimary)}>
                  {selectedDevelopmentRoles.length > 0
                    ? selectedDevelopmentRoles.join(', ')
                    : t('No development roles set')}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {DEVELOPMENT_ROLE_OPTIONS.map((role) => {
                    const isSelected = selectedDevelopmentRoles.includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleDevelopmentRole(role)}
                        className={cn(
                          'rounded-lg border px-2.5 py-1.5 text-xs transition-colors',
                          theme.borderPrimary,
                          isSelected
                            ? cn(theme.controlActive, theme.selectionRing, theme.textPrimary)
                            : cn(theme.bgPrimary, theme.textSecondary)
                        )}
                      >
                        {t(role)}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className={cn('text-[11px] leading-5', theme.textSecondary)}>
                {t('Choose one or more development roles when the workstream is development.')}
              </div>
            </div>
          ) : null}

          <label className="space-y-1">
            <span className={cn('flex items-center gap-2 text-xs font-medium', theme.textSecondary)}>
              <Shield size={14} />
              {t('Visibility')}
            </span>
            <select
              data-testid="node-metadata-visibility-select"
              value={selectedVisibilityValue}
              onChange={(event) => handleVisibilitySelection(event.target.value)}
              disabled={readOnly}
              className={cn('w-full rounded-lg border px-3 py-2 text-sm', theme.bgPrimary, theme.borderPrimary, theme.textPrimary)}
            >
              {visibilityOptions.map(option => (
                <option key={`${option.mode}:${option.value}`} value={`${option.mode}:${option.value}`}>
                  {option.mode === 'request'
                    ? t('{{tier}} (approval)', { tier: t(option.value) })
                    : t(option.value)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {canManageVisibleToAccounts ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className={cn('text-xs font-medium', theme.textSecondary)}>{t('Visible to specific project members')}</span>
            <span className={cn('text-[11px]', theme.textSecondary)}>
              {visibleToCount > 0
                ? t('{{count}} selected', { count: visibleToCount })
                : t('No account restrictions')}
            </span>
          </div>
          {readOnly ? (
            <div className={cn('w-full rounded-lg border px-3 py-2 text-sm', theme.bgPrimary, theme.borderPrimary, theme.textPrimary)}>
              {visibleToCount > 0
                ? (visibleToAccountIds || []).map((accountId) => getAssigneeDisplayName(accountId)).join(', ')
                : t('No account restrictions')}
            </div>
          ) : (
            <MultiAssigneeInput
              assignees={visibleToAccountIds || []}
              onChange={onVisibleToAccountIdsChange}
              suggestions={projectMemberIds}
              labelForAssignee={getAssigneeDisplayName}
              allowCustomValues={false}
              placeholder={t('Type a name or open the list')}
              emptySuggestionsMessage={
                projectId
                  ? t('No project members are available for this project yet.')
                  : t('Select a project to load project members.')
              }
              className="rounded-lg"
            />
          )}
          <div className={cn('text-[11px] leading-5', theme.textSecondary)}>
            {projectMemberIds.length > 0
              ? t('Search or open the dropdown to choose people from this project’s member list.')
              : t('Add project access first before restricting visibility here.')}
          </div>
        </div>
        ) : null}

        {showApplyToDescendants ? (
          <label className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-xs', theme.bgPrimary, theme.borderPrimary, theme.textSecondary)}>
            <input
              type="checkbox"
              checked={applyToDescendants}
              disabled={readOnly}
              onChange={(event) => onApplyToDescendantsChange?.(event.target.checked)}
            />
            <span>{t('Apply this metadata to descendants')}</span>
          </label>
        ) : null}

        {(typeof classificationConfidence === 'number' || rationale) ? (
          <div className={cn('rounded-lg border px-3 py-2 text-xs', theme.bgPrimary, theme.borderPrimary, theme.textSecondary)}>
            {typeof classificationConfidence === 'number' ? `${t('Confidence')}: ${Math.round(classificationConfidence * 100)}%` : null}
            {typeof classificationConfidence === 'number' && rationale ? ' · ' : null}
            {rationale || ''}
          </div>
        ) : null}
      </div>

      <VisibilityApprovalRequestDialog
        isOpen={!!pendingRequestVisibilityTier}
        theme={theme}
        targetLabel={targetLabel || t('this item')}
        requestedVisibilityTier={pendingRequestVisibilityTier || 'internal'}
        defaultScope={defaultRequestScope}
        showSubtreeOption={showApplyToDescendants}
        isSubmitting={isSubmittingRequest}
        errorMessage={requestError}
        onClose={() => {
          setPendingRequestVisibilityTier(null);
          setRequestError(null);
        }}
        onSubmit={async ({ scope, rationale: requestRationale }) => {
          if (!pendingRequestVisibilityTier || !projectId || !nodeId || !onRequestVisibilityTier) {
            setPendingRequestVisibilityTier(null);
            return;
          }
          setIsSubmittingRequest(true);
          setRequestError(null);
          try {
            await onRequestVisibilityTier({
              projectId,
              nodeId,
              visibilityTier: pendingRequestVisibilityTier,
              scope,
              rationale: requestRationale,
            });
            setPendingRequestVisibilityTier(null);
          } catch (error) {
            // Keep the dialog open with the reason; the rejection used to escape unhandled.
            setRequestError(t('Failed to send approval request: {{message}}', {
              message: error instanceof Error && error.message ? error.message : t('Unknown error'),
            }));
          } finally {
            setIsSubmittingRequest(false);
          }
        }}
      />
    </>
  );
};
